import React, { useRef, Children } from 'react';
import { motion, useScroll, useTransform, type Variants } from 'framer-motion';
import { Button } from './ui/Button';
import { LotusMark } from './ui/LotusMark';
import { BOOKING_URL } from './constants';
// Pre-computed positions for the barely-visible floating dust particles.
const PARTICLES = [{
  left: '12%',
  top: '24%',
  size: 3,
  delay: 0,
  duration: 11
}, {
  left: '22%',
  top: '68%',
  size: 2,
  delay: 2.4,
  duration: 13
}, {
  left: '38%',
  top: '40%',
  size: 4,
  delay: 1.2,
  duration: 14
}, {
  left: '52%',
  top: '78%',
  size: 2,
  delay: 3.1,
  duration: 12
}, {
  left: '66%',
  top: '30%',
  size: 3,
  delay: 0.8,
  duration: 15
}, {
  left: '74%',
  top: '60%',
  size: 2,
  delay: 2,
  duration: 10
}, {
  left: '84%',
  top: '38%',
  size: 4,
  delay: 1.6,
  duration: 16
}, {
  left: '90%',
  top: '72%',
  size: 2,
  delay: 3.6,
  duration: 13
}];
// Cinematic staggered entrance: headline -> subheadline -> CTA.
const containerVariants: Variants = {
  hidden: {},
  show: {
    transition: {
      delayChildren: 1.1,
      staggerChildren: 0.32
    }
  }
};
const revealVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 28,
    filter: 'blur(6px)'
  },
  show: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 1.1,
      ease: [0.21, 0.47, 0.32, 0.98]
    }
  }
};
export function Hero() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start']
  });
  // Elegant parallax: layers drift at different speeds as you scroll away.
  const logoY = useTransform(scrollYProgress, [0, 1], ['0%', '24%']);
  const logoScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '40%']);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const raysOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.2]);
  return <section ref={sectionRef} className="relative min-h-[100svh] flex items-center justify-center overflow-hidden bg-background" data-id="element-121">
      {/* Warm beige base wash */}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: 'radial-gradient(120% 90% at 70% 18%, #FBF8F3 0%, #F2EBE0 45%, #E8DECF 100%)'
    }} data-id="element-122" />

      {/* Volumetric light rays entering from the top right */}
      <motion.div style={{
      opacity: raysOpacity
    }} className="absolute inset-0 pointer-events-none" data-id="element-123">
        <div className="absolute -top-1/3 right-0 w-[80%] h-[150%] blur-2xl" style={{
        background: 'conic-gradient(from 200deg at 85% 0%, rgba(255,255,255,0.0) 0deg, rgba(255,252,244,0.55) 18deg, rgba(222,198,140,0.28) 30deg, rgba(255,255,255,0) 55deg)'
      }} data-id="element-124" />
        <div className="absolute top-0 right-0 w-[55%] h-[80%] blur-3xl" style={{
        background: 'radial-gradient(60% 60% at 90% 5%, rgba(255,253,247,0.7) 0%, rgba(255,253,247,0) 70%)'
      }} data-id="element-125" />
      </motion.div>

      {/* Subtle cinematic leaf shadows */}
      <div className="absolute top-0 left-0 w-[42%] h-full opacity-[0.10] pointer-events-none mix-blend-multiply" data-id="element-126">
        <img src="https://images.unsplash.com/photo-1502082553048-f009c37129b9?auto=format&fit=crop&q=80&w=1200" alt="" aria-hidden="true" className="w-full h-full object-cover blur-[2px] [mask-image:linear-gradient(to_right,black,transparent)]" data-id="element-127" />
      </div>

      {/* Oversized lotus centerpiece — faint, glassy, breathing + floating */}
      <motion.div style={{
      y: logoY,
      scale: logoScale
    }} className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-128">
        <motion.div initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 0.1,
        scale: 1
      }} transition={{
        duration: 2.6,
        ease: [0.21, 0.47, 0.32, 0.98]
      }} data-id="element-129">
          {/* Breathing + floating wrapper */}
          <motion.div animate={{
          y: [0, -4, 0],
          scale: [1, 1.03, 1]
        }} transition={{
          duration: 10,
          ease: 'easeInOut',
          repeat: Infinity
        }} className="rounded-full backdrop-blur-[2px]" data-id="element-130">
            <LotusMark className="w-[88vw] max-w-[760px] h-auto drop-shadow-[0_8px_40px_rgba(201,169,97,0.25)]" data-id="element-131" />
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Soft luxury light bloom over the center */}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: 'radial-gradient(40% 40% at 50% 45%, rgba(255,253,247,0.45) 0%, rgba(255,253,247,0) 70%)'
    }} data-id="element-132" />

      {/* Barely-visible floating dust particles */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-133">
        {PARTICLES.map((p, i) => <motion.span key={i} className="absolute rounded-full bg-gold-light" style={{
        left: p.left,
        top: p.top,
        width: p.size,
        height: p.size
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: [0, 0.5, 0],
        y: [0, -28, 0]
      }} transition={{
        duration: p.duration,
        delay: p.delay,
        ease: 'easeInOut',
        repeat: Infinity
      }} data-id="element-134" />)}
      </div>

      {/* Hero content */}
      <motion.div style={{
      y: contentY,
      opacity: contentOpacity
    }} className="container mx-auto px-6 md:px-12 relative z-20" data-id="element-135">
        <motion.div variants={containerVariants} initial="hidden" animate="show" className="max-w-3xl mx-auto text-center flex flex-col items-center" data-id="element-136">
          <motion.div variants={revealVariants} data-id="element-137">
            <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full bg-surface/60 backdrop-blur-md border border-border/80 mb-8 shadow-sm" data-id="element-138">
              <span className="w-1.5 h-1.5 rounded-full bg-gold" data-id="element-139" />
              <span className="text-[0.7rem] font-medium tracking-[0.25em] uppercase text-foreground/80" data-id="element-140">
                Medicina Estética Avanzada
              </span>
            </div>
          </motion.div>

          <motion.h1 variants={revealVariants} className="font-serif font-light text-5xl md:text-7xl lg:text-[5.5rem] text-foreground leading-[1.05] tracking-tight mb-8" data-id="element-141">
            Recupera la mejor versión de tu{' '}
            <span className="text-gold italic" data-id="element-142">piel</span>
          </motion.h1>

          <motion.p variants={revealVariants} className="text-lg md:text-xl text-muted-foreground font-light leading-relaxed mb-12 max-w-xl" data-id="element-143">
            Medicina estética avanzada con resultados naturales y protocolos
            personalizados.
          </motion.p>

          <motion.div variants={revealVariants} data-id="element-144">
            <Button href={BOOKING_URL} target="_blank" rel="noopener noreferrer" size="lg" className="tracking-[0.15em] uppercase text-sm" data-id="element-145">
              Agendar Valoración
            </Button>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Elegant scroll indicator */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 2.6,
      duration: 1.2
    }} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-3 pointer-events-none" data-id="element-146">
        <span className="text-[0.6rem] tracking-[0.3em] uppercase text-muted-foreground" data-id="element-147">
          Descubrir
        </span>
        <div className="w-[1px] h-12 bg-gradient-to-b from-gold/60 to-transparent overflow-hidden" data-id="element-148">
          <motion.div className="w-full h-3 bg-gold" animate={{
          y: [-12, 48]
        }} transition={{
          duration: 2.4,
          ease: 'easeInOut',
          repeat: Infinity
        }} data-id="element-149" />
        </div>
      </motion.div>
    </section>;
}